import { Badge } from '../../../components/Badge';
import { useRbacStore } from '../../../store/rbacStore';
import type { AssetRequest } from '../../../store/assetStore';

/**
 * True when the user holds an Assets grant company-wide (auth grants, module
 * 'Assets' — the same check the assets service enforces). Re-renders when the
 * grants load.
 */
export function useAssetGrant(resource: string, action: 'View' | 'Create' | 'Edit' | 'Delete' = 'Edit') {
  useRbacStore((s) => s.permissions);
  useRbacStore((s) => s.roles);
  return useRbacStore.getState().getScope('Assets', resource, action) === 'Company';
}

/** Company-wide edit rights on the register (assign/return/retire, recover…). */
export function useIsAssetManager() {
  return useAssetGrant('Assets', 'Edit');
}

export const REQUEST_STATUS_META: Record<
  AssetRequest['status'],
  { label: string; tone: 'warning' | 'info' | 'success' | 'destructive' }
> = {
  pending: { label: 'Pending Approval', tone: 'warning' },
  approved: { label: 'Approved', tone: 'info' },
  fulfilled: { label: 'Fulfilled', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'destructive' },
};

export function RequestStatus({ request }: { request: AssetRequest }) {
  const s = REQUEST_STATUS_META[request.status] ?? { label: request.status, tone: 'info' as const };
  return (
    <Badge variant={s.tone}>
      {s.label}{request.status === 'fulfilled' && request.assigned_asset_code ? ` · ${request.assigned_asset_code}` : ''}
    </Badge>
  );
}

export const PRIORITY_TONE: Record<AssetRequest['priority'], 'muted' | 'info' | 'warning' | 'destructive'> = {
  low: 'muted', medium: 'info', high: 'warning', urgent: 'destructive',
};
